// Point-id sets as stored in edit ops ("rle1"): the sorted, distinct pids as
// alternating LEB128 varints gap, run, gap, run, ... (gap = non-members before
// the run, may be 0), base64. Compact for ranges and for scattered points.
// Same encoding as prep/edits.py.

export interface PidSet { enc: 'rle1'; n: number; b64: string }

export function toB64(b: Uint8Array) {
  let s = '';
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s);
}
export function fromB64(s: string) {
  const bin = atob(s), b = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i);
  return b;
}

/** Encode sorted, distinct pids. */
export function encodeSet(pids: ArrayLike<number>): PidSet {
  const out: number[] = [];
  const put = (v: number) => { while (v >= 0x80) { out.push((v & 0x7f) | 0x80); v = Math.floor(v / 128); } out.push(v); };
  let next = 0;
  for (let i = 0; i < pids.length;) {
    const s = pids[i];
    if (s < next) throw new Error('pid set not sorted and distinct');
    let e = i + 1;
    while (e < pids.length && pids[e] === pids[e - 1] + 1) e++;
    put(s - next); put(e - i);
    next = pids[e - 1] + 1;
    i = e;
  }
  return { enc: 'rle1', n: pids.length, b64: toB64(new Uint8Array(out)) };
}

/** Decode to sorted pids; checks the count and that every pid is below N. */
export function decodeSet(s: PidSet, N: number): Uint32Array {
  if (!s || s.enc !== 'rle1' || typeof s.b64 !== 'string' || !Number.isInteger(s.n) || s.n < 0 || s.n > N) throw new Error('bad pid set');
  const b = fromB64(s.b64), out = new Uint32Array(s.n);
  let i = 0, at = 0, k = 0, gap = true;
  while (i < b.length) {
    let v = 0, mul = 1, byte: number;
    do {
      if (i >= b.length || mul > 2 ** 35) throw new Error('bad pid set varint');
      byte = b[i++]; v += (byte & 0x7f) * mul; mul *= 128;
    } while (byte & 0x80);
    if (gap) at += v;
    else {
      if (v === 0 || k + v > s.n || at + v > N) throw new Error('pid set out of range');
      for (let j = 0; j < v; j++) out[k++] = at++;
    }
    gap = !gap;
  }
  if (k !== s.n) throw new Error(`pid set has ${k} pids, says ${s.n}`);
  return out;
}

/** Pids where mask is set (mask indexed by pid). */
export function maskToPids(mask: Uint8Array): Uint32Array {
  let n = 0;
  for (let i = 0; i < mask.length; i++) if (mask[i]) n++;
  const out = new Uint32Array(n);
  for (let i = 0, j = 0; i < mask.length; i++) if (mask[i]) out[j++] = i;
  return out;
}

export const maskToSet = (mask: Uint8Array) => encodeSet(maskToPids(mask));
